/* Ask to join by workspace ID — STORY-008, and the last of STORY-004's three
 * routes to be built.
 *
 * One field, and the interesting work is all in what the screen may say.
 *
 * The server answers identically whether or not the workspace exists — an
 * Organisation ID is a weak secret, and refusing to confirm one is what keeps
 * it from being an enumeration oracle. So this screen may never name the
 * workspace, never confirm it is real, and never report that a request "was
 * delivered". Copy that promised any of those would move the leak from the API
 * to the UI, which is the same leak.
 *
 * What it can do is set the expectation BEFORE the submit rather than explain
 * it after: an Owner has to approve, nothing happens until they do, and no
 * access is granted meanwhile.
 */

import { requestToJoinRequestSchema } from '@eventuley/contracts';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { AuthScreen, Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useCurrentUser } from '../../auth/index.js';
import { useRequestToJoin } from '../api/index.js';

/* Shown in the invalid-ID message. A shape is far more use than a rule: "it
 * looks like this" tells somebody who pasted half a URL what went wrong. */
const EXAMPLE_ID = '018f2a4c-1e00-7000-8000-4f2b9c1d3e5a';

export function AskToJoinScreen() {
  const t = useTranslate();
  const ask = useRequestToJoin();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();

  const [id, setId] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    /* Cleared before every attempt. A confirmation left over from the previous
     * one sat above the next attempt's refusal, so the screen said "your
     * request was sent" and "too many requests" at the same time. */
    setSent(false);

    /* Parsed with the CONTRACT's schema, not a regex written here. The server
     * validates independently and is the only thing trusted — this is a
     * convenience, and its value is catching a typo while the person can still
     * see what they typed, rather than letting it become a request that waits
     * forever for an approval nobody can give. */
    const parsed = requestToJoinRequestSchema.safeParse({ organisationId: id.trim() });
    if (!parsed.success) {
      setInvalid(true);
      return;
    }

    setInvalid(false);

    /* Already in it? Go there — STORY-008 says so, and the alternative is
     * telling somebody standing in a workspace to wait for an approval that
     * will never come. The SERVER cannot make this distinction: it answers the
     * uniform 202 and must, because saying "you are already a Member" would
     * confirm the workspace is real. The client can, because it already holds
     * this person's own list of workspaces — the enumeration constraint binds
     * what the server reveals, not what somebody already knows. */
    const held = (user?.memberships ?? []).find(
      (membership) => membership.organisationId === parsed.data.organisationId,
    );
    if (held) {
      void navigate({
        to: '/orgs/$organisationId/calendar',
        params: { organisationId: held.organisationId },
      });
      return;
    }

    ask.mutate(parsed.data.organisationId, { onSuccess: () => setSent(true) });
  };

  const rateLimited = ask.error instanceof ApiError && ask.error.code === 'rate_limited';

  return (
    <AuthScreen
      title={t('workspace.join.title')}
      brand={t('app.name')}
      subtitle={t('workspace.join.body')}
    >
      {sent ? (
        /* Deliberately conditional prose. "If that workspace exists" is true of
         * both outcomes, which is the only kind of confirmation this screen is
         * allowed to give. */
        <Callout tone="info">{t('workspace.join.sent')}</Callout>
      ) : null}

      {rateLimited ? <Callout tone="warning">{t('workspace.join.rate_limited')}</Callout> : null}

      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('workspace.join.id_label')}
          autoComplete="off"
          hint={t('workspace.join.id_hint')}
          error={invalid ? t('workspace.join.id_invalid', { example: EXAMPLE_ID }) : undefined}
          value={id}
          onChange={(event) => setId(event.target.value)}
        />

        <div className="auth__actions">
          <Button type="submit" block busy={ask.isPending}>
            {ask.isPending ? t('workspace.join.submitting') : t('workspace.join.submit')}
          </Button>
          <Link to="/next" className="btn btn--ghost btn--block">
            {t('workspace.join.back')}
          </Link>
        </div>
      </form>
    </AuthScreen>
  );
}
